import React, { useState, useMemo, useEffect } from 'react';
import { CustomQuizConfig, Question } from '../types';

interface CustomQuizModalProps {
    isOpen: boolean;
    onStart: (config: CustomQuizConfig) => void;
    onCancel: () => void;
    allQuestions: Question[];
    displayLanguage: 'pt' | 'en' | 'es';
}

const CustomQuizModal: React.FC<CustomQuizModalProps> = ({ isOpen, onStart, onCancel, allQuestions, displayLanguage }) => {
    const uniqueDomains = useMemo(() => Array.from(new Set(allQuestions.map(q => q.domain).filter((d): d is string => !!d))), [allQuestions]);
    
    const [selectedDomains, setSelectedDomains] = useState<string[]>(() => uniqueDomains);
    const [questionCount, setQuestionCount] = useState<number>(10);
    const [isAllSelected, setIsAllSelected] = useState(true);

    const maxQuestions = useMemo(() => {
        if (selectedDomains.length === 0) return 0;
        return allQuestions.filter(q => q.domain && selectedDomains.includes(q.domain)).length;
    }, [selectedDomains, allQuestions]);
    
    useEffect(() => {
        // Adjust question count if it becomes invalid after changing domains
        if (questionCount > maxQuestions) {
            setQuestionCount(maxQuestions);
        }
        if (questionCount === 0 && maxQuestions > 0) {
            setQuestionCount(10 > maxQuestions ? maxQuestions : 10);
        }
    }, [maxQuestions, questionCount]);

    useEffect(() => {
        if (isOpen) {
            const initialDomains = Array.from(new Set(allQuestions.map(q => q.domain).filter((d): d is string => !!d)));
            setSelectedDomains(initialDomains);
            setIsAllSelected(true);
        }
    }, [isOpen, allQuestions]);

    const handleDomainToggle = (domain: string) => {
        const newSelection = selectedDomains.includes(domain)
            ? selectedDomains.filter(d => d !== domain)
            : [...selectedDomains, domain];
        setSelectedDomains(newSelection);
        setIsAllSelected(newSelection.length === uniqueDomains.length);
    };
    
    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecking = e.target.checked;
        setIsAllSelected(isChecking);
        if (isChecking) {
            setSelectedDomains(uniqueDomains);
        } else {
            setSelectedDomains([]);
        }
    }

    const handleStart = () => {
        onStart({ domains: selectedDomains, questionCount });
    };

    if (!isOpen) return null;

    const title =
        displayLanguage === 'en'
            ? 'Create Custom Exam'
            : displayLanguage === 'es'
              ? 'Crear examen personalizado'
              : 'Criar Simulado Personalizado';
    const step1 =
        displayLanguage === 'en'
            ? '1. Select domains'
            : displayLanguage === 'es'
              ? '1. Selecciona dominios'
              : '1. Selecione os Domínios';
    const selectAll =
        displayLanguage === 'en' ? 'Select all' : displayLanguage === 'es' ? 'Seleccionar todo' : 'Selecionar Todos';
    const step2 =
        displayLanguage === 'en'
            ? '2. Select number of questions'
            : displayLanguage === 'es'
              ? '2. Elige la cantidad de preguntas'
              : '2. Selecione o Número de Questões';
    const maxHint =
        displayLanguage === 'en'
            ? `Maximum questions for selected domains: ${maxQuestions}`
            : displayLanguage === 'es'
              ? `Máximo de preguntas para los dominios seleccionados: ${maxQuestions}`
              : `Máximo de questões para os domínios selecionados: ${maxQuestions}`;
    const cancel =
        displayLanguage === 'en' ? 'Cancel' : displayLanguage === 'es' ? 'Cancelar' : 'Cancelar';
    const start =
        displayLanguage === 'en'
            ? 'Start exam'
            : displayLanguage === 'es'
              ? 'Iniciar examen'
              : 'Iniciar Simulado';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-50 flex justify-center items-center p-4" aria-modal="true" role="dialog">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-2xl text-left animate-fade-in">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{title}</h2>
                
                <div className="mb-6">
                    <h3 className="text-lg font-semibold text-orange-400 mb-3">{step1}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="bg-gray-100 dark:bg-slate-700/50 p-3 rounded-md">
                            <label className="flex items-center cursor-pointer">
                                <input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} className="form-checkbox h-5 w-5 bg-gray-200 dark:bg-slate-600 border-gray-400 dark:border-slate-500 rounded text-orange-500 focus:ring-orange-500" />
                                <span className="ml-3 font-bold text-gray-800 dark:text-slate-200">{selectAll}</span>
                            </label>
                        </div>
                        {uniqueDomains.map(domain => (
                            <div key={domain} className="bg-gray-100 dark:bg-slate-700/50 p-3 rounded-md">
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedDomains.includes(domain)}
                                        onChange={() => handleDomainToggle(domain)}
                                        className="form-checkbox h-5 w-5 bg-gray-200 dark:bg-slate-600 border-gray-400 dark:border-slate-500 rounded text-orange-500 focus:ring-orange-500"
                                    />
                                    <span className="ml-3 text-gray-700 dark:text-slate-300 text-sm">{domain}</span>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mb-8">
                    <h3 className="text-lg font-semibold text-orange-400 mb-3">{step2}</h3>
                    <div className="flex items-center gap-4">
                        <input
                            type="range"
                            min="1"
                            max={maxQuestions > 0 ? maxQuestions : 1}
                            value={questionCount}
                            onChange={(e) => setQuestionCount(Number(e.target.value))}
                            disabled={maxQuestions === 0}
                            className="w-full h-2 bg-gray-300 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer disabled:bg-gray-200 dark:disabled:bg-slate-800"
                        />
                        <span className="font-bold text-gray-800 dark:text-white text-lg bg-gray-200 dark:bg-slate-700 px-3 py-1 rounded-md min-w-[60px] text-center">{maxQuestions > 0 ? questionCount : 0}</span>
                    </div>
                     <p className="text-sm text-gray-500 dark:text-slate-400 mt-2">{maxHint}</p>
                </div>

                <div className="flex justify-end gap-4">
                    <button onClick={onCancel} className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-slate-600 dark:hover:bg-slate-500 dark:text-white font-bold py-2 px-6 rounded-lg transition-colors">
                        {cancel}
                    </button>
                    <button onClick={handleStart} disabled={selectedDomains.length === 0 || questionCount === 0 || maxQuestions === 0} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-6 rounded-lg transition-colors disabled:bg-slate-600 disabled:cursor-not-allowed">
                        {start}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CustomQuizModal;